import axios, { AxiosResponse } from 'axios';
import {
  getTasksSolutionSheetStatusIdList,
  ISolutionCard,
  ISolutionCardsTable,
  DeleteTasksSolutionSheetEnumResult,
  IUpdateSolutionData,
  ITasksSolutionSheetDto,
} from '../../models/solutionCard.types';
import { ExamTasksCoordinates } from '../../models/assessment.types';
import EnvWindow from '../../utils/env';
import { addParam, addParamsList, downloadFile, splitParams } from '../../utils/servicesHelper';
import { ExamTaskImageZone, imageZonesRequest } from '../../models/imageZone.types';
import { ExamineeStatus, ExamineeResponse } from '../../pages/ExamineeList/ExamineeList.types';
import { ExamineeImport, SheetStatistic, SystemId } from './images.types';
import { imageZone } from '../../pages/Sessions/ImageZonesEditor/ZonesList/ZonesList.types';
import { IExamineeInfoDto } from '../../components/ExamineeDetails/ExamineeDetails.types';

export async function getSolutionCardDetails(id: string): Promise<ISolutionCard> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__TASKS_SOLUTION_SHEET_API_URL}/${id}`);
  return response.data;
}

export async function getExamTaskPages(taskId: string): Promise<ExamTasksCoordinates> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__EXAM_TASK_API_URL}/${taskId}/taskpage`);
  return response.data;
}

export async function getSolutionSheetsApi(
  signal?: AbortSignal,
  pageSize = 20,
  page = 0,
  sortByProperty?: string,
  isDescending?: boolean,
  tasksSolutionSheetId?: string,
  bundleNumber?: string,
  bundleName?: string,
  bundleSheetCode?: string,
  examCommitteeFilter?: string,
  examineeSchoolCode?: string,
  examineeIdentity?: string,
  bundleImportFrom?: string,
  bundleImportTo?: string,
  processingDateFrom?: string,
  processingDateTo?: string,
  status?: string,
): Promise<ISolutionCardsTable> {
  const tasksSolutionSheetStatusIdList = status ? getTasksSolutionSheetStatusIdList(status) : [];
  let params = '';
  params += addParam('tasksSolutionSheetId', tasksSolutionSheetId ? tasksSolutionSheetId : '');
  params += addParam('bundleNumber', bundleNumber ? bundleNumber : '');
  params += addParam('bundleName', bundleName ? bundleName : '');
  params += addParam('bundleSheetCode', bundleSheetCode ? bundleSheetCode : '');
  params += addParamsList('examCommitteeFilter', examCommitteeFilter ? examCommitteeFilter.split(',') : []);
  params += addParam('schoolCode', examineeSchoolCode ? examineeSchoolCode : '');
  params += addParam('examineeIdentity', examineeIdentity ? examineeIdentity : '');
  params += addParam('bundleImportFrom', bundleImportFrom ? bundleImportFrom : '');
  params += addParam('bundleImportTo', bundleImportTo ? bundleImportTo : '');
  params += addParam('processingDateFrom', processingDateFrom ? processingDateFrom : '');
  params += addParam('processingDateTo', processingDateTo ? processingDateTo : '');
  params += addParamsList('statuses', tasksSolutionSheetStatusIdList.length > 0 ? tasksSolutionSheetStatusIdList : []);
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? isDescending : '');
  const response = await axios.get(
    `${EnvWindow.REACT_APP_IMAGES__TASKS_SOLUTION_SHEET_API_URL}?page=${page}&pageSize=${pageSize}${params}`,
    { signal: signal ?? undefined },
  );
  return response.data;
}

export async function exportSolutionSheetsApi(
  signal?: AbortSignal,
  sortByProperty?: string,
  isDescending?: boolean,
  tasksSolutionSheetId?: string,
  bundleNumber?: string,
  bundleName?: string,
  bundleSheetCode?: string,
  examCommitteeFilter?: string,
  examineeSchoolCode?: string,
  examineeIdentity?: string,
  bundleImportFrom?: string,
  bundleImportTo?: string,
  processingDateFrom?: string,
  processingDateTo?: string,
  status?: string,
): Promise<Blob> {
  const tasksSolutionSheetStatusIdList = status ? getTasksSolutionSheetStatusIdList(status) : [];
  let params = '';
  params += addParam('tasksSolutionSheetId', tasksSolutionSheetId ? tasksSolutionSheetId : '');
  params += addParam('bundleNumber', bundleNumber ? bundleNumber : '');
  params += addParam('bundleName', bundleName ? bundleName : '');
  params += addParam('bundleSheetCode', bundleSheetCode ? bundleSheetCode : '');
  params += addParamsList('examCommitteeFilter', examCommitteeFilter ? examCommitteeFilter.split(',') : []);
  params += addParam('schoolCode', examineeSchoolCode ? examineeSchoolCode : '');
  params += addParam('examineeIdentity', examineeIdentity ? examineeIdentity : '');
  params += addParam('bundleImportFrom', bundleImportFrom ? bundleImportFrom : '');
  params += addParam('bundleImportTo', bundleImportTo ? bundleImportTo : '');
  params += addParam('processingDateFrom', processingDateFrom ? processingDateFrom : '');
  params += addParam('processingDateTo', processingDateTo ? processingDateTo : '');
  params += addParamsList('statuses', tasksSolutionSheetStatusIdList.length > 0 ? tasksSolutionSheetStatusIdList : []);
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? isDescending : '');
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet/Export?${params}`, {
    signal: signal ?? undefined,
  });
  return response.data;
}

export function updateTasksSolutionSheetPageRotationAngle(
  tasksSolutionSheetId: string,
  pageNumber: number,
  rotationAngle: number,
): Promise<AxiosResponse> {
  const response = axios.patch(
    `${EnvWindow.REACT_APP_IMAGES__TASKS_SOLUTION_SHEET_API_URL}/${tasksSolutionSheetId}/page/${pageNumber}/angle/${rotationAngle}`,
  );
  return response;
}

export async function updateTasksSolutionSheetDetails(
  tasksSolutionSheetId: string,
  examineeIdentifier: string,
): Promise<AxiosResponse<IUpdateSolutionData>> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_IMAGES__TASKS_SOLUTION_SHEET_API_URL}/${tasksSolutionSheetId}`,
    {
      examineeIdentifier: examineeIdentifier,
    },
  );
  return response;
}

export async function deleteTasksSolutionSheetDetails(
  tasksSolutionSheetId: string,
): Promise<DeleteTasksSolutionSheetEnumResult> {
  const response = await axios.delete(
    `${EnvWindow.REACT_APP_IMAGES__TASKS_SOLUTION_SHEET_API_URL}/${tasksSolutionSheetId}`,
  );
  return response.data;
}

export async function replacePageImage(pageUrl: string, newImage: Blob): Promise<AxiosResponse<boolean>> {
  const requestBody = new FormData();
  requestBody.append('File', newImage);
  const response = await axios({
    method: 'PUT',
    url: pageUrl,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });
  return response;
}

export async function getExamSheetImageZones(examSheetId: string): Promise<AxiosResponse<ExamTaskImageZone[]>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__EXAM_SHEET_API_URL}/${examSheetId}/examTask`);
  return response;
}

export async function deleteExamTaskImageZones(examTaskId: string): Promise<AxiosResponse> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_IMAGES__EXAM_TASK_API_URL}/${examTaskId}/TaskZoneImage`);
  return response;
}

export async function deleteExamScoringSchemaImageZones(examScoringSchemaId: string): Promise<AxiosResponse> {
  const response = await axios.delete(
    `${EnvWindow.REACT_APP_IMAGES__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}/TaskZoneImage`,
  );
  return response;
}

export async function getTaskImageZones(examTaskId: string): Promise<AxiosResponse<imageZone[]>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__TASK_IMAGE_ZONE_API_URL}/examTask/${examTaskId}`);
  return response;
}

export async function getExamScoringSchemaImageZone(examScoringSchemaId: string): Promise<AxiosResponse<imageZone[]>> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_IMAGES__TASK_IMAGE_ZONE_API_URL}/examScoringSchema/${examScoringSchemaId}`,
  );
  return response;
}

export async function updateExamTaskImageZone(
  examTaskId: string,
  examSheetId: string,
  zones: imageZonesRequest[],
): Promise<AxiosResponse> {
  const response = await axios.put(`${EnvWindow.REACT_APP_IMAGES__EXAM_TASK_API_URL}/${examTaskId}/imageZone`, {
    imageZonesRequest: zones,
    examSheetId,
  });
  return response;
}

export async function updateExamScoringSchemaImageZone(
  examScoringSchemaId: string,
  examSheetId: string,
  zones: imageZonesRequest[],
): Promise<AxiosResponse> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_IMAGES__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}/imageZone`,
    {
      imageZonesRequest: zones,
      examSheetId,
    },
  );
  return response;
}

export async function getExamineeList(
  signal?: AbortSignal,
  page = 0,
  pageSize = 20,
  sortByProperty?: string,
  isDescending?: boolean,
  examCommittee?: string,
  schoolCode?: string,
  examineeCode?: string,
  examineePesel?: string,
  examName?: string,
  sheetCode?: string,
  room?: string,
  importDateFrom?: string,
  importDateTo?: string,
  status?: ExamineeStatus[],
  adjustments?: string,
  adjustmentTypes?: string,
  adjustmentPermissions?: string,
): Promise<AxiosResponse<ExamineeResponse>> {
  let params = '';
  params += splitParams('examCommittee', examCommittee) ?? '';
  params += addParam('schoolCode', schoolCode ? schoolCode : '');
  params += addParam('examineeCode', examineeCode ? examineeCode : '');
  params += addParam('examineePesel', examineePesel ? examineePesel : '');
  params += addParam('examName', examName ? examName : '');
  params += addParam('room', room ? room : '');
  params += addParam('sheetCode', sheetCode ? sheetCode : '');
  params += addParam('importDateFrom', importDateFrom ? importDateFrom : '');
  params += addParam('importDateTo', importDateTo ? importDateTo : '');
  params += splitParams('status', status?.toString()) ?? '';
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : 'importDate');
  params += addParam('isDescending', isDescending ? 'true' : 'false');
  params += addParam(
    'adjustments',
    adjustments && adjustments.length ? (adjustments === 'TAK' ? 'true' : adjustments === 'NIE' ? 'false' : '') : '',
  );
  params += addParam(
    'adjustmentTypeIds',
    adjustmentTypes && adjustmentTypes?.length ? adjustmentTypes : '',
  );
  params += addParam(
    'adjustmentPermissionsIds',
    adjustmentPermissions && adjustmentPermissions?.length ? adjustmentPermissions : ''
  );

  const response = await axios.get(
    `${EnvWindow.REACT_APP_IMAGES__EXAMINEE_API_URL}?page=${page}&pageSize=${pageSize}${params}`,
    { signal: signal ?? undefined },
  );
  return response;
}

export async function deleteExaminee(examineeId: string | undefined | string[]) {
  const response = await axios.delete(`${EnvWindow.REACT_APP_IMAGES__EXAMINEE_API_URL}`, {
    data: typeof (examineeId) === 'string' ? [examineeId] : examineeId
  });
  return response.data;
}

export async function deleteExamineeList(
  sortByProperty?: string,
  isDescending?: boolean,
  examCommittee?: string,
  schoolCode?: string,
  examineeCode?: string,
  examineePesel?: string,
  examName?: string,
  sheetCode?: string,
  room?: string,
  importDateFrom?: string,
  importDateTo?: string,
  status?: ExamineeStatus[],
): Promise<AxiosResponse<string | undefined>> {
  let params = '';
  params += splitParams('examCommittee', examCommittee) ?? '';
  params += addParam('schoolCode', schoolCode ? schoolCode : '');
  params += addParam('examineeCode', examineeCode ? examineeCode : '');
  params += addParam('examineePesel', examineePesel ? examineePesel : '');
  params += addParam('examName', examName ? examName : '');
  params += addParam('room', room ? room : '');
  params += addParam('sheetCode', sheetCode ? sheetCode : '');
  params += addParam('importDateFrom', importDateFrom ? importDateFrom : '');
  params += addParam('importDateTo', importDateTo ? importDateTo : '');
  params += splitParams('status', status?.toString()) ?? '';
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : 'importDate');
  params += addParam('isDescending', isDescending ? 'true' : 'false');

  const response = await axios.delete(`${EnvWindow.REACT_APP_IMAGES__EXAMINEE_API_URL}/filter?${params}`);
  return response;
}

export async function exportExamineeApi(
  signal?: AbortSignal,
  sortByProperty?: string,
  isDescending?: boolean,
  examCommittee?: string,
  schoolCode?: string,
  examineeCode?: string,
  examineePesel?: string,
  examName?: string,
  sheetCode?: string,
  room?: string,
  importDateFrom?: string,
  importDateTo?: string,
  status?: ExamineeStatus[],
  adjustments?: string,
  adjustmentTypes?: string,
  adjustmentPermissions?: string,
): Promise<Blob> {
  let params = '';
  params += splitParams('examCommittee', examCommittee) ?? '';
  params += addParam('schoolCode', schoolCode ? schoolCode : '');
  params += addParam('examineeCode', examineeCode ? examineeCode : '');
  params += addParam('examineePesel', examineePesel ? examineePesel : '');
  params += addParam('examName', examName ? examName : '');
  params += addParam('room', room ? room : '');
  params += addParam('sheetCode', sheetCode ? sheetCode : '');
  params += addParam('importDateFrom', importDateFrom ? importDateFrom : '');
  params += addParam('importDateTo', importDateTo ? importDateTo : '');
  params += splitParams('status', status?.toString()) ?? '';
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : 'importDate');
  params += addParam('isDescending', isDescending ? 'true' : 'false');
  params += addParam(
    'adjustments',
    adjustments && adjustments.length ? (adjustments === 'TAK' ? 'true' : adjustments === 'NIE' ? 'false' : '') : '',
  );

  params += addParam(
    'adjustmentTypeIds',
    adjustmentTypes && adjustmentTypes?.length ? adjustmentTypes : '',
  );
  params += addParam(
    'adjustmentPermissionsIds',
    adjustmentPermissions && adjustmentPermissions?.length ? adjustmentPermissions : ''
  );
  
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamineeExport?${params}`, {
    signal: signal ?? undefined,
  });
  return response.data;
}

export async function getExamineeStatistic(examSheetId: string): Promise<AxiosResponse<SheetStatistic>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__EXAM_SHEET_API_URL}/${examSheetId}/stats`);
  return response;
}

export async function getExamineeDetails(examineeId: string): Promise<AxiosResponse<IExamineeInfoDto>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES_API_URL}/Examinee/${examineeId}/details`);
  return response;
}

export async function importExamineeApi(examSheetId: string, systemName: string): Promise<AxiosResponse<ExamineeImport>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamSheet/${examSheetId}/${systemName}`);
  return response;
}

export async function importExaminee(examSheetId: string): Promise<AxiosResponse<ExamineeImport>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_IMAGES__EXAM_SHEET_API_URL}/${examSheetId}/examineeImport`);
  return response;
}
export async function uploadFile(file: File, examSheetId: string, systemId: SystemId): Promise<AxiosResponse> {
  const requestBody = new FormData();
  requestBody.append('file', file);
  const response = await axios({
    method: 'POST',
    url: `${EnvWindow.REACT_APP_IMAGES__EXAM_SHEET_API_URL}/${examSheetId}/System/${systemId}/File`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });
  return response;
}

export async function getImportApiExamineeCount(examSheetId: string): Promise<AxiosResponse<string>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_IMAGES__EXAM_SHEET_API_URL}/${examSheetId}/examineeCount`);
  return response;
}

export async function saveImportExaminee(
  importId: string,
  examSheetId: string,
  completion: boolean,
): Promise<AxiosResponse<ExamineeImport>> {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamineeImport/${importId}/${examSheetId}/${completion}`,
  );
  return response;
}

export async function processCards(tasksSolutionSheetIds: string[]): Promise<AxiosResponse<number>> {
  const response = await axios.patch(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet/Processing`, {
    tasksSolutionSheetIds,
  });
  return response;
}

export const downloadDeleteTasksSolutionSheetReport = (correlationId: string, fileName: string) => {
  downloadFile(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet/CorrelationId/${correlationId}`, fileName);
};

export async function removeCards(tasksSolutionSheets: ITasksSolutionSheetDto[]): Promise<AxiosResponse<boolean>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet`, {
    data: { tasksSolutionSheets },
  });
  return response;
}

export async function removeCardsByFiltres(
  tasksSolutionSheetId?: string,
  bundleNumber?: string,
  bundleName?: string,
  bundleSheetCode?: string,
  examCommitteeFilter?: string,
  examineeSchoolCode?: string,
  examineeIdentity?: string,
  bundleImportFrom?: string,
  bundleImportTo?: string,
  processDateFrom?: string,
  processDateTo?: string,
  status?: string,
): Promise<AxiosResponse<boolean>> {
  const tasksSolutionSheetStatusIdList = status ? getTasksSolutionSheetStatusIdList(status) : [];
  const response = await axios.delete(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet/filter`, {
    data: {
      tasksSolutionSheetId,
      bundleNumber,
      bundleName,
      bundleSheetCode,
      examCommitteeFilter: examCommitteeFilter ? examCommitteeFilter.split(',').map((item) => parseInt(item)) : [],
      schoolCode: examineeSchoolCode,
      examineeIdentity,
      bundleImportFrom: bundleImportFrom && bundleImportFrom?.length > 0 ? bundleImportFrom : null,
      bundleImportTo: bundleImportTo && bundleImportTo?.length > 0 ? bundleImportFrom : null,
      processDateFrom: processDateFrom && processDateFrom?.length > 0 ? processDateFrom : null,
      processDateTo: processDateTo && processDateTo?.length > 0 ? processDateTo : null,
      statuses: tasksSolutionSheetStatusIdList.length > 0 ? tasksSolutionSheetStatusIdList : [],
    }
  });
  return response;
}

export async function processAllCards(
  tasksSolutionSheetId?: string,
  bundleNumber?: string,
  bundleName?: string,
  bundleSheetCode?: string,
  examCommitteeFilter?: string,
  examineeSchoolCode?: string,
  examineeIdentity?: string,
  bundleImportFrom?: string,
  bundleImportTo?: string,
  processDateFrom?: string,
  processDateTo?: string,
  status?: string,
): Promise<AxiosResponse<number>> {
  const tasksSolutionSheetStatusIdList = status ? getTasksSolutionSheetStatusIdList(status) : [];
  const response = await axios.patch(`${EnvWindow.REACT_APP_IMAGES_API_URL}/TasksSolutionSheet/Processing/Query`, {
    tasksSolutionSheetId,
    bundleNumber,
    bundleName,
    bundleSheetCode,
    examCommitteeFilter: examCommitteeFilter ? examCommitteeFilter.split(',').map((item) => parseInt(item)) : [],
    schoolCode: examineeSchoolCode,
    examineeIdentity,
    bundleImportFrom: bundleImportFrom && bundleImportFrom?.length > 0 ? bundleImportFrom : null,
    bundleImportTo: bundleImportTo && bundleImportTo?.length > 0 ? bundleImportFrom : null,
    processDateFrom: processDateFrom && processDateFrom?.length > 0 ? processDateFrom : null,
    processDateTo: processDateTo && processDateTo?.length > 0 ? processDateTo : null,
    statuses: tasksSolutionSheetStatusIdList.length > 0 ? tasksSolutionSheetStatusIdList : [],
  });
  return response;
}

export const downloadExamineeImportReport = (importExamineeFromSioepkzState: string, fileName: string) => {
  downloadFile(
    `${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamineeImport/ImportExamineeState/${importExamineeFromSioepkzState}`,
    fileName,
  );
};
